import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@shared/components/ui/button';
import {
  User,
  FileText,
  FolderKanban,
  Users,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ShieldCheck,
  Clock,
  Loader2,
  UserCheck,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';

/**
 * Portal Welcome / Onboarding Screen
 *
 * Shown after first login (or whenever setup is incomplete).
 * Walks the supplier through the 5 steps needed before they can
 * check in on site:
 *
 *  1. Complete profile (name, company, photo, vehicle reg)
 *  2. Read & complete site induction
 *  3. Create or join a project
 *  4. Add your team members
 *  5. Get your digital pass (QR badge)
 */

const STEPS = [
  {
    id: 'profile',
    number: 1,
    icon: User,
    title: 'Complete Your Profile',
    description:
      'Add your full name, company details, a photo, contact number, and vehicle registration. This information is used to verify your identity on site.',
    action: 'Set Up Profile',
    route: '/portal/profile',
    why: 'Required for site access',
    time: '2 minutes',
  },
  {
    id: 'induction',
    number: 2,
    icon: FileText,
    title: 'Read the Site Induction',
    description:
      'Review the site safety brief and complete the induction process. This covers emergency procedures, PPE requirements, hazards, and site rules.',
    action: 'Start Induction',
    route: '/portal/inductions',
    why: 'Legally required before entering site',
    time: '5–10 minutes',
  },
  {
    id: 'project',
    number: 3,
    icon: FolderKanban,
    title: 'Create or Join a Project',
    description:
      'Create a project for your work on site, or join an existing one. Projects group your team together for attendance tracking and compliance.',
    action: 'View Projects',
    route: '/portal/projects',
    why: 'Tracks attendance and work scope',
    time: '3 minutes',
  },
  {
    id: 'team',
    number: 4,
    icon: Users,
    title: 'Add Your Team Members',
    description:
      'Invite the people working with you on site. Each team member will receive a portal invitation so they can complete their own induction and get a site pass.',
    action: 'Add Team Members',
    route: '/portal/projects',
    why: 'Everyone on site needs their own pass',
    time: '2 minutes per person',
  },
  {
    id: 'teamInductions',
    number: 5,
    icon: UserCheck,
    title: 'Awaiting Team Inductions',
    description:
      'Your team members need to complete their own site inductions before the project can be submitted for review. You will automatically move to the next step once everyone is inducted.',
    action: 'Check Progress',
    route: '/portal/projects',
    why: 'All site workers must be inducted',
    time: 'Depends on your team',
  },
  {
    id: 'badge',
    number: 6,
    icon: BadgeCheck,
    title: 'Get Your Digital Site Pass',
    description:
      'Once your project has been reviewed and approved by the site team, your digital QR pass is issued automatically. Show this at the site kiosk or gate for check-in and check-out.',
    action: 'View My Pass',
    route: '/portal/digital-badge',
    why: 'Your site access credential',
    time: 'Once project approved',
  },
];

/**
 * Condensed 4-step journey for team members (added by a lead).
 * They don't create projects or add team members — they only need to:
 *  1. Complete profile
 *  2. Read the site induction
 *  3. View their project (read-only)
 *  4. Get their digital pass
 */
const TEAM_MEMBER_STEPS = [
  STEPS[0], // 1 — Complete Your Profile
  STEPS[1], // 2 — Read the Site Induction
  {
    id: 'project',
    number: 3,
    icon: FolderKanban,
    title: 'View Your Project',
    description:
      'Your project lead has added you to a project on site. Review the project details, site location, and the team you will be working alongside.',
    action: 'View Project',
    route: '/portal/projects',
    why: 'Confirms your role on site',
    time: '1 minute',
  },
  {
    ...STEPS[5], // 4 — Get Your Digital Site Pass (renumbered from 6)
    number: 4,
  },
];

const PortalWelcome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useOutletContext();

  // If arriving from a profile save, optimistically treat profile as done so
  // step 2 is unlocked immediately — the async check below will confirm/correct.
  const arrivedFromProfileSave = location.state?.expandStep === 'induction';

  const [completionStatus, setCompletionStatus] = useState({
    profile:        arrivedFromProfileSave,
    induction:      false,
    project:        false,
    team:           false,
    teamInductions: false,
    badge:          false,
  });
  const [loading, setLoading]               = useState(true);
  const [expandedStep, setExpandedStep]     = useState(
    location.state?.expandStep || null
  );
  // If user has no projects as lead, hide project/team steps (team member only)
  const [isTeamMemberOnly, setIsTeamMemberOnly] = useState(false);

  const email = session?.person?.email;
  const name  = session?.person?.name || email;

  useEffect(() => {
    if (email) checkCompletion();
    // Re-run whenever user navigates back to this page so step status is fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, location.key]);

  const checkCompletion = async () => {
    try {
      // ── Determine the current invite cycle ────────────────────────────────
      // Every new project invitation creates a fresh induction_submissions row.
      // All 5 steps are evaluated relative to the most recent invite so that
      // returning users must go through the full flow again for each new project.
      //
      // NOTE: direct induction_submissions queries are blocked by RLS for portal
      // users (they use a custom JWT, not Supabase auth). Use the SECURITY DEFINER
      // RPC to bypass RLS.
      const { data: recentSubs, error: subsError } = await supabase
        .rpc('portal_get_submission_history', { p_email: email });

      // The start of the current cycle = most recent submission's created_at.
      // Multiple briefs sent together share the same timestamp.
      const currentCycleStart = recentSubs?.[0]?.created_at || null;

      // ── Is the user a project lead anywhere? ──────────────────────────────
      // Team members (not leads) skip Steps 3 & 4 (project + team).
      const { data: anyLeadProjects } = await supabase
        .from('projects')
        .select('id')
        .eq('project_lead_email', email)
        .limit(1);
      const userIsTeamMemberOnly = !anyLeadProjects?.length;
      setIsTeamMemberOnly(userIsTeamMemberOnly);

      // ── Profile ───────────────────────────────────────────────────────────
      const { data: profile } = await supabase
        .from('portal_profiles')
        .select('person_name, person_phone, person_company, photo_url, vehicle_reg, attending_site_id, profile_completed_at')
        .eq('person_email', email)
        .maybeSingle();

      const profileFieldsDone = !!(
        profile?.person_name &&
        profile?.person_phone &&
        profile?.person_company &&
        profile?.photo_url &&
        profile?.vehicle_reg &&
        profile?.attending_site_id
      );

      // Use profile_completed_at — updated every time the user confirms their
      // profile. Once the 20260223 migration is deployed, profile_confirmed_at
      // can be added back here for stricter per-cycle enforcement.
      const confirmedAt = profile?.profile_completed_at;
      const profileDone =
        profileFieldsDone &&
        !!confirmedAt &&
        (!currentCycleStart || new Date(confirmedAt) >= new Date(currentCycleStart));

      // ── Induction ─────────────────────────────────────────────────────────
      // Load active briefs for the user's registered site to get their current
      // version. The person is only "inducted" if every active brief has a
      // submission where brief_version matches the brief's CURRENT version and
      // status is submitted or approved.
      // Using profile.attending_site_id as the site reference for global guide.
      let inductionDone = false;
      try {
        const siteId = profile?.attending_site_id;
        if (!siteId) {
          inductionDone = false; // No site assigned yet
        } else {
          const { data: activeBriefs } = await supabase
            .from('induction_briefs')
            .select('id, version')
            .eq('depot_id', siteId)
            .eq('is_active', true);

          if (!activeBriefs || activeBriefs.length === 0) {
            inductionDone = true; // No active briefs at this site
          } else {
            inductionDone = activeBriefs.every(brief =>
              (recentSubs || []).some(s =>
                s.brief_id      === brief.id &&
                s.brief_version === brief.version &&
                (s.status === 'submitted' || s.status === 'approved')
              )
            );
          }
        }
      } catch (e) {
        console.error('[PortalWelcome] induction version check failed', e);
      }

      // ── Project (as lead or team member this cycle) ───────────────────────
      let leadProjectsThisCycle = [];
      let memberRowsThisCycle = [];
      let projectDone = false;
      if (currentCycleStart) {
        const { data: leadProjects } = await supabase
          .from('projects')
          .select('id, status')
          .eq('project_lead_email', email)
          .gte('created_at', currentCycleStart)
          .limit(5);

        const { data: memberRows } = await supabase
          .from('project_team_members')
          .select('id, project_id, projects(id, status)')
          .eq('person_email', email)
          .gte('invited_at', currentCycleStart)
          .limit(5);

        leadProjectsThisCycle = leadProjects || [];
        memberRowsThisCycle   = memberRows   || [];
        projectDone =
          leadProjectsThisCycle.length + memberRowsThisCycle.length > 0;
      }

      // ── Team members (leads only; team members auto-pass this step) ───────
      let teamDone = false;
      if (currentCycleStart) {
        if (leadProjectsThisCycle.length) {
          const projectIds = leadProjectsThisCycle.map(p => p.id);
          const { data: teamMembers } = await supabase
            .from('project_team_members')
            .select('id')
            .in('project_id', projectIds)
            .neq('person_email', email) // exclude the lead themselves
            .limit(1);
          teamDone = (teamMembers?.length || 0) > 0;
        } else {
          // They joined as a team member this cycle — team step passes automatically
          teamDone = projectDone;
        }
      }

      // ── Team inductions (leads only) ─────────────────────────────────────
      // Step 5: all team members in the lead's project(s) must have submitted
      // or approved inductions before the project can go for admin review.
      // Uses portal_get_project_induction_summary SECURITY DEFINER RPC so the
      // portal JWT can read other people's induction_submissions.
      let teamInductionsDone = false;
      if (!userIsTeamMemberOnly && leadProjectsThisCycle.length > 0) {
        try {
          const { data: inductionSummary } = await supabase
            .rpc('portal_get_project_induction_summary', { p_lead_email: email });
          const thisCycleIds = new Set(leadProjectsThisCycle.map(p => p.id));
          const relevant = (inductionSummary || []).filter(s => thisCycleIds.has(s.project_id));
          // all_inducted = true means lead + every team member has submitted/approved
          teamInductionsDone = relevant.length > 0 && relevant.every(s => s.all_inducted === true);
        } catch (e) {
          // RPC not yet deployed — safe to leave false
          // RPC not deployed yet — safe to leave teamInductionsDone as false
        }
      }
      // Team members skip this step; their 4-step flow doesn't include it
      if (userIsTeamMemberOnly) teamInductionsDone = true;

      // ── Badge — only after admin approves the project ─────────────────────
      // Neither the lead nor any team member gets a digital pass until the site
      // team has reviewed and approved the project submission.
      const leadProjectApproved   = leadProjectsThisCycle.some(p => p.status === 'approved');
      const memberProjectApproved = memberRowsThisCycle.some(
        r => r.projects?.status === 'approved'
      );
      // For leads: requires team inductions done AND project approved by admin.
      // For team members: requires own induction done AND project approved by admin.
      const badgeDone = userIsTeamMemberOnly
        ? (inductionDone && memberProjectApproved)
        : (teamInductionsDone && leadProjectApproved);

      setCompletionStatus({
        profile:        profileDone,
        induction:      inductionDone,
        project:        projectDone,
        team:           teamDone,
        teamInductions: teamInductionsDone,
        badge:          badgeDone,
      });

      // Auto-expand the first incomplete step
      const filteredSteps = userIsTeamMemberOnly ? TEAM_MEMBER_STEPS : STEPS;
      const doneMap = { profile: profileDone, induction: inductionDone, project: projectDone, team: teamDone, teamInductions: teamInductionsDone, badge: badgeDone };
      const firstIncomplete = filteredSteps.find(s => !doneMap[s.id]);
      // Only auto-expand if not already set by router navigation state
      if (firstIncomplete) setExpandedStep(prev => prev || firstIncomplete.id);
    } catch (err) {
      console.error('PortalWelcome: completion check failed', err);
    } finally {
      setLoading(false);
    }
  };

  const visibleSteps = isTeamMemberOnly ? TEAM_MEMBER_STEPS : STEPS;
  const completedCount = visibleSteps.filter(s => completionStatus[s.id]).length;
  const allDone = visibleSteps.every(s => completionStatus[s.id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Portal label */}
        <div className="flex items-center gap-2 mb-5">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <ShieldCheck className="h-4 w-4 text-primary" />
          </div>
          <span className="text-xs font-bold text-primary uppercase tracking-widest">
            Supplier Portal
          </span>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-2">
          Welcome{name ? `, ${name.split(' ')[0]}` : ''} 👋
        </h1>
        <p className="text-base font-medium text-muted-foreground leading-relaxed">
          Before you can access site, there are a few things to set up. Follow the steps
          below — it takes around{' '}
          <strong className="text-foreground">15 minutes</strong> to complete everything.
        </p>

        {/* Progress bar */}
        <div className="mt-5 p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between text-sm mb-2.5">
            <span className="font-semibold text-foreground">Setup progress</span>
            <span className="font-bold text-primary">{completedCount} / {visibleSteps.length} complete</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / visibleSteps.length) * 100}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="h-full rounded-full bg-primary"
            />
          </div>
        </div>
      </motion.div>

      {/* ── All-Done Banner ──────────────────────────────────────────── */}
      <AnimatePresence>
        {allDone && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="p-5 rounded-xl bg-green-50 border border-green-300 flex items-center gap-4"
          >
            <div className="p-2.5 rounded-full bg-green-100 flex-shrink-0">
              <CheckCircle2 className="h-6 w-6 text-green-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-green-900">You&apos;re all set!</p>
              <p className="text-sm font-medium text-green-800">All steps complete. You have full site access.</p>
            </div>
            <Button
              onClick={() => navigate('/portal/dashboard')}
              className="bg-green-700 hover:bg-green-800 text-white flex-shrink-0"
            >
              Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Steps ───────────────────────────────────────────────────── */}
      <div className="space-y-2">
        {visibleSteps.map((step, index) => {
          const done       = completionStatus[step.id];
          const isExpanded = expandedStep === step.id;
          const Icon       = step.icon;
          const locked     = index > 0 && !completionStatus[visibleSteps[index - 1].id];
          const displayNum = index + 1;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
            >
              <div
                className={`rounded-xl border overflow-hidden transition-all duration-200
                  ${ done
                    ? 'border-green-300 bg-green-50'
                    : locked
                      ? 'border-border bg-muted/30 opacity-60'
                      : 'border-primary/40 bg-card shadow-sm'
                  }`}
              >
                {/* Row — always visible */}
                <button
                  type="button"
                  className="w-full text-left px-4 py-3.5 flex items-center gap-3 disabled:cursor-not-allowed"
                  onClick={() => !locked && setExpandedStep(isExpanded ? null : step.id)}
                  disabled={locked}
                >
                  {/* Status circle */}
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
                    ${ done ? 'bg-green-100' : locked ? 'bg-muted' : 'bg-primary/10' }`}
                  >
                    {done
                      ? <CheckCircle2 className="h-5 w-5 text-green-700" />
                      : <Icon className={`h-4 w-4 ${locked ? 'text-muted-foreground' : 'text-primary'}`} />
                    }
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-xs font-bold uppercase tracking-wider
                        ${ done ? 'text-green-700' : 'text-muted-foreground' }`}>
                        Step {displayNum}
                      </span>
                      {done && (
                        <span className="inline-flex items-center rounded-full bg-green-100 border border-green-300 px-2 py-0 text-xs font-bold text-green-800">
                          Complete
                        </span>
                      )}
                      {!done && !locked && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 border border-primary/30 px-2 py-0 text-xs font-bold text-primary">
                          Action needed
                        </span>
                      )}
                      {!done && locked && (
                        <span className="inline-flex items-center rounded-full bg-muted border border-border px-2 py-0 text-xs font-semibold text-muted-foreground">
                          Complete step {displayNum - 1} first
                        </span>
                      )}
                    </div>
                    <p className={`font-bold text-sm
                      ${ done ? 'text-muted-foreground line-through' : 'text-foreground' }`}>
                      {step.title}
                    </p>
                  </div>

                  {/* Chevron */}
                  {!locked && (
                    isExpanded
                      ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                </button>

                {/* Expanded detail */}
                <AnimatePresence>
                  {isExpanded && !locked && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-0 border-t border-border">
                        <p className="text-sm font-medium text-muted-foreground leading-relaxed mt-3 mb-4">
                          {step.description}
                        </p>

                        <div className="flex items-center gap-5 mb-4 text-xs font-semibold text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                            {step.why}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            {step.time}
                          </span>
                        </div>

                        <Button
                          size="sm"
                          onClick={() => navigate(step.route)}
                          variant={done ? 'outline' : 'default'}
                          className={done ? 'border-border text-foreground' : ''}
                        >
                          {done ? 'Review' : step.action}
                          <ArrowRight className="ml-2 h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex items-center justify-between pt-2"
      >
        <p className="text-sm font-medium text-muted-foreground">
          You can revisit this guide any time from the dashboard.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/portal/dashboard')}
          className="text-muted-foreground hover:text-foreground"
        >
          Skip to Dashboard <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </motion.div>

    </div>
  );
};

export default PortalWelcome;
